<script setup lang="ts" generic="TValue extends string | number">
import { computed, type Component } from 'vue'
import {
  ElSelect,
  ElSelectV2,
  ElOption,
  ElOptionGroup,
  ElCheckbox,
  ElIcon,
  ElButton
} from 'element-plus'
import { Loading } from '@element-plus/icons-vue'

import { Inline } from '@/shared/ui/Inline'
import { StatusDot } from '@/shared/ui/StatusDot'

/**
 * The select, over options that are **all in hand**: an enum, or a catalogue fetched whole.
 * A field whose list is searched on the server is `RemoteSelect`, which pages and cannot
 * offer a true "select all".
 *
 * Generic over the value its options carry, so a select built from a string enum hands that
 * enum to its handler instead of a bare `string` to cast back; the type is inferred from
 * `options`, or from `modelValue`. Build the options with `labelsToOptions` for a filter
 * (string values, since they ride the URL) or `idsToOptions` for a form field, and bind
 * numeric ids through `useNumericIds`.
 *
 * A long list renders itself virtually without being asked — see `VIRTUALIZE_FROM`.
 */
const props = withDefaults(
  defineProps<{
    modelValue?: TValue | null | TValue[]
    options: Array<{
      label: string
      // `null` is a real option value here: a filter's "All" entry clears the filter,
      // and `modelValue` has always accepted null to match.
      value: TValue | null
      id?: string | number
      name?: string
      /** Listed but not pickable — a hint row, or a value this context cannot offer. */
      disabled?: boolean
      /** A `StatusDot` before the label in the dropdown; the virtual list has no option slot. */
      status?: boolean | null
      statusText?: string
      /** A glyph before the label in the dropdown; like `status`, not on the virtual list. */
      icon?: Component
      /**
       * Heading this option is filed under. Set it on every option to get a grouped
       * dropdown; see `isGrouped` for why every one and not just some.
       */
      group?: string
    }>
    placeholder?: string
    clearable?: boolean
    disabled?: boolean
    /**
     * Why the option list is empty, when the answer is "the request was refused" rather
     * than "there is nothing". A plain `Select` over a fully-fetched catalogue gets no
     * `useListError` — that path belongs to `RemoteSelect` — so without this a 403 opens
     * an empty dropdown with no reason and no way back. Pair it with `@retry`.
     */
    loadError?: string
    /** Multiple selection (v-model is array) */
    multiple?: boolean
    filterable?: boolean
    loading?: boolean
    /** Leave unset to inherit the size from ElConfigProvider */
    size?: '' | 'small' | 'default' | 'large'
    /** Stretch to the full width of the parent (Element Plus defaults to 128px) */
    block?: boolean
    /**
     * Offer a checkbox above the list that picks everything at once. Only for `multiple`,
     * and only where the options are all in hand — a remote select holds one page.
     */
    selectAll?: boolean
    /**
     * A line under the checkbox saying what "all" covers here. A select that holds every
     * option needs none; one that holds a page has to say so rather than imply otherwise.
     */
    selectAllNote?: string
    /**
     * Let the user enter a value of their own — a domain, a subid — that no option offers.
     * Needs `filterable`, which is what the typed value is read out of, and pairs with
     * `default-first-option` so Enter takes the entry rather than whatever the query
     * happened to match. What comes back is the typed **string**.
     */
    allowCreate?: boolean
    /**
     * Render the options through a virtual list. Left unset it turns itself on for a
     * long list, which is what a caller normally wants — see `VIRTUALIZE_FROM`.
     */
    virtualized?: boolean
  }>(),
  {
    // Spelled out because Vue casts an absent boolean prop to `false` only when it has no
    // default at all: without this line "unset" and "off" are the same value, the rule
    // below never applies, and every list in the app stays on the eager path.
    virtualized: undefined
  }
)

/**
 * How long a list has to get before it is rendered virtually.
 *
 * Element Plus mounts every `ElOption` up front — a select's dropdown is `persistent`, so
 * its content exists whether or not it was ever opened — so a screen carrying many such
 * selects pays for the whole cross-product before it can paint: a hundred rows each holding
 * a hundred-option picker is ten thousand component instances.
 *
 * The virtual path takes the options as plain data, renders only the rows in view and reads
 * the picked label off that same data, so its dropdown need not exist until it is opened.
 * Costs: the option **slot** is not rendered there, and `popup-scroll` is not emitted —
 * which is why `RemoteSelect` opts out of it entirely.
 */
const VIRTUALIZE_FROM = 60

const isVirtualized = computed(
  () => props.virtualized ?? props.options.length > VIRTUALIZE_FROM
)

/** `o.value` may be null for an "All" entry; ElOption needs a concrete value. */
function optionValue(o: {
  value: string | number | null
  id?: string | number
}) {
  return (o.value ?? o.id) as string | number
}

/** The virtual list reads options as data, so the `{id, name}` fallback resolves here. */
const virtualOptions = computed(() =>
  props.options.map((o) => ({
    label: o.label ?? o.name,
    value: optionValue(o),
    disabled: o.disabled
  }))
)

/**
 * Group the dropdown only when *every* option says where it belongs.
 *
 * A partly-grouped list has no good answer — Element Plus headings are not optional, so
 * the ungrouped remainder would need a heading this component would have to invent. All
 * or nothing keeps the rule one a caller can predict, and a list that has never carried
 * `group` renders exactly as it did before.
 */
const pickable = computed(() =>
  props.options.filter((o) => !o.disabled).map((o) => optionValue(o))
)

const picked = computed(() =>
  Array.isArray(props.modelValue) ? props.modelValue : []
)

const allPicked = computed(
  () =>
    pickable.value.length > 0 && picked.value.length >= pickable.value.length
)

/** Some but not all — the box says "there is more to take" rather than lying either way. */
const somePicked = computed(() => picked.value.length > 0 && !allPicked.value)

const offersAll = computed(
  () => !!props.selectAll && !!props.multiple && pickable.value.length > 0
)

const isGrouped = computed(
  () => props.options.length > 0 && props.options.every((o) => !!o.group)
)

/** Groups in the order they first appear, so the caller's ordering is the one shown. */
const groups = computed(() => {
  const byLabel = new Map<string, typeof props.options>()

  for (const option of props.options) {
    const label = option.group as string
    const group = byLabel.get(label)

    if (group) group.push(option)
    else byLabel.set(label, [option])
  }

  return [...byLabel].map(([label, options]) => ({ label, options }))
})

const virtualGroups = computed(() =>
  groups.value.map((group) => ({
    label: group.label,
    options: group.options.map((o) => ({
      label: o.label ?? o.name,
      value: optionValue(o),
      disabled: o.disabled
    }))
  }))
)

// Element Plus knows nothing about the options it was handed, so it types what it emits
// as the widest thing a select can hold. Narrowing that back to `TValue` is this
// wrapper's job and happens once, here — which is the point of the generic.
const emit = defineEmits<{
  (e: 'update:modelValue', v: TValue | null | TValue[]): void
  // Same value as `update:modelValue`, but only when the *user* picked it — a form that
  // has to reset a dependent field listens to this rather than to every model write.
  (e: 'change', v: TValue | null | TValue[]): void
  (e: 'retry'): void
}>()

type Emitted = TValue | null | TValue[]

/**
 * An empty list means something else once the field takes its own values: not "there is
 * nothing here" but "type it". Left undefined otherwise, so Element Plus keeps its own
 * wording — and `$attrs` still wins for a caller that wants its own.
 */
const noDataText = computed(() =>
  props.allowCreate ? 'Enter the value and press enter' : undefined
)

function toggleAll(checked: boolean | string | number) {
  emit('update:modelValue', (checked ? pickable.value : []) as Emitted)
  emit('change', (checked ? pickable.value : []) as Emitted)
}
</script>

<template>
  <ElSelectV2
    v-if="isVirtualized"
    class="shared-select"
    :class="{ 'shared-select--block': block }"
    :model-value="modelValue ?? undefined"
    :options="isGrouped ? virtualGroups : virtualOptions"
    :placeholder="placeholder"
    :clearable="clearable"
    :disabled="disabled"
    :multiple="multiple"
    :filterable="filterable"
    :loading="loading"
    :persistent="false"
    :allow-create="allowCreate"
    :default-first-option="allowCreate"
    :no-data-text="noDataText"
    v-bind="$attrs"
    :size="size"
    @update:model-value="emit('update:modelValue', $event as Emitted)"
    @change="emit('change', $event as Emitted)"
  >
    <template v-if="offersAll || $slots.header" #header>
      <ElCheckbox
        v-if="offersAll"
        :model-value="allPicked"
        :indeterminate="somePicked"
        @change="toggleAll"
      >
        All
      </ElCheckbox>
      <div v-if="selectAllNote" class="shared-select__note">
        {{ selectAllNote }}
      </div>
      <div v-if="loadError" class="shared-select__error">
        {{ loadError }}
        <ElButton link type="primary" size="small" @click="emit('retry')">
          Try again
        </ElButton>
      </div>
      <slot name="header" />
    </template>

    <template #loading>
      <div class="select-loading">
        <ElIcon class="is-loading" :size="20">
          <Loading />
        </ElIcon>
      </div>
    </template>
  </ElSelectV2>

  <ElSelect
    v-else
    class="shared-select"
    :class="{ 'shared-select--block': block }"
    :model-value="modelValue ?? undefined"
    :placeholder="placeholder"
    :clearable="clearable"
    :disabled="disabled"
    :multiple="multiple"
    :filterable="filterable"
    :loading="loading"
    :allow-create="allowCreate"
    :default-first-option="allowCreate"
    :no-data-text="noDataText"
    v-bind="$attrs"
    :size="size"
    @update:model-value="emit('update:modelValue', $event as Emitted)"
    @change="emit('change', $event as Emitted)"
  >
    <template v-if="offersAll || $slots.header" #header>
      <ElCheckbox
        v-if="offersAll"
        :model-value="allPicked"
        :indeterminate="somePicked"
        @change="toggleAll"
      >
        All
      </ElCheckbox>
      <div v-if="selectAllNote" class="shared-select__note">
        {{ selectAllNote }}
      </div>
      <div v-if="loadError" class="shared-select__error">
        {{ loadError }}
        <ElButton link type="primary" size="small" @click="emit('retry')">
          Try again
        </ElButton>
      </div>
      <slot name="header" />
    </template>

    <template #loading>
      <div class="select-loading">
        <ElIcon class="is-loading" :size="20">
          <Loading />
        </ElIcon>
      </div>
    </template>
    <template v-if="isGrouped">
      <ElOptionGroup
        v-for="group in groups"
        :key="group.label"
        :label="group.label"
      >
        <ElOption
          v-for="o in group.options"
          :key="o.value ?? o.id"
          :label="o.label ?? o.name"
          :value="optionValue(o)"
          :disabled="o.disabled"
        >
          <Inline
            v-if="o.status !== undefined || o.icon"
            :gap="8"
            align="center"
          >
            <StatusDot
              v-if="o.status !== undefined"
              :status="o.status"
              :text="o.statusText"
            />
            <ElIcon v-if="o.icon"><component :is="o.icon" /></ElIcon>
            {{ o.label ?? o.name }}
          </Inline>
        </ElOption>
      </ElOptionGroup>
    </template>
    <ElOption
      v-for="o in options"
      v-else
      :key="o.value ?? o.id"
      :label="o.label ?? o.name"
      :value="optionValue(o)"
      :disabled="o.disabled"
    >
      <Inline v-if="o.status !== undefined || o.icon" :gap="8" align="center">
        <StatusDot
          v-if="o.status !== undefined"
          :status="o.status"
          :text="o.statusText"
        />
        <ElIcon v-if="o.icon"><component :is="o.icon" /></ElIcon>
        {{ o.label ?? o.name }}
      </Inline>
    </ElOption>
  </ElSelect>
</template>

<style lang="scss" scoped>
.shared-select--block {
  width: 100%;
}

.shared-select__error {
  padding-top: 2px;
  font-size: 12px;
  line-height: 1.3;
  color: var(--el-color-danger);
  white-space: normal;
}

.shared-select__note {
  padding-top: 2px;
  font-size: 12px;
  line-height: 1.3;
  color: var(--el-text-color-secondary);
  white-space: normal;
}

.select-loading {
  display: flex;
  justify-content: center;
  padding: 12px 0;
  color: var(--el-color-primary);
}
</style>
